<template>
  <div class="pa-2">
    <va-card>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h5 class="mb-0">Actionable Information Report (AIR)</h5>
        <div v-if="crimeData.isAirSubmitted || crimeData.isAirApproved">
          <b-button variant="outline-primary" @click="downloadPDF">
            <i class="fa fa-download mr-1"></i> Download AIR PDF
          </b-button>
        </div>
      </div>

      <div v-if="!isAdmin && !crimeData.isAirSubmitted">
        <!-- Analyst Form -->
        <b-form @submit.prevent="submitAIR">
          <b-form-group label="Information Summary" label-class="font-weight-bold">
            <b-form-textarea
              v-model="airData.infoSummary"
              rows="5"
              placeholder="Enter summary of information..."
              required
            ></b-form-textarea>
          </b-form-group>

          <b-form-group label="Point of Contact" label-class="font-weight-bold">
            <b-form-input
              v-model="airData.pointOfContact"
              placeholder="Details of source/contact..."
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Location Details" label-class="font-weight-bold">
            <b-form-input
              v-model="airData.location.exactLocation"
              placeholder="Specific coordinates or landmarks..."
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Supporting Evidence Links" label-class="font-weight-bold" description="Separate multiple URLs with commas">
            <b-form-textarea
              v-model="evidenceLinks"
              rows="2"
              placeholder="https://example.com/image1, https://example.com/video1..."
            ></b-form-textarea>
          </b-form-group>

          <div class="mt-4 text-right">
            <b-button type="submit" variant="success" class="px-4">
              Submit for Final Approval
            </b-button>
          </div>
        </b-form>
      </div>

      <div v-else-if="crimeData.isAirSubmitted || crimeData.isAirApproved">
        <!-- Data Display -->
        <div class="p-3 border rounded bg-light mb-3">
          <b-row class="mb-2">
            <b-col cols="3" class="font-weight-bold">Report ID:</b-col>
            <b-col class="text-primary font-weight-bold">{{ crimeData.airData.airUniqueId }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="3" class="font-weight-bold">Report Status:</b-col>
            <b-col>
              <va-badge v-if="crimeData.isAirApproved" color="success">APPROVED</va-badge>
              <va-badge v-else color="warning">PENDING APPROVAL</va-badge>
            </b-col>
          </b-row>
          <hr />
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Information Summary:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.infoSummary }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Point of Contact:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.pointOfContact }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Location Details:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.location ? crimeData.airData.location.exactLocation : 'N/A' }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Supporting Evidence:</b-col>
            <b-col cols="12" class="mt-1">
              <div v-if="crimeData.airData.supportingEvidence && crimeData.airData.supportingEvidence.length">
                <ul class="pl-0">
                  <li v-for="(item, index) in crimeData.airData.supportingEvidence" :key="index">
                    <a :href="item.url" target="_blank" class="text-info">{{ item.url }}</a>
                  </li>
                </ul>
              </div>
              <span v-else class="text-muted">No evidence links provided.</span>
            </b-col>
          </b-row>
        </div>

        <div v-if="isAdmin && crimeData.isAirSubmitted && !crimeData.isAirApproved" class="text-right">
          <b-button variant="success" @click="approveAIR" class="px-4">
            Approve AIR
          </b-button>
        </div>
      </div>

      <div v-else class="text-center py-5 text-muted">
        <i class="fa fa-info-circle fa-3x mb-3"></i>
        <p>No AIR has been submitted yet for this case.</p>
      </div>
    </va-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: ["crimeData", "appUser"],
  data() {
    return {
      airData: {
        infoSummary: "",
        pointOfContact: "",
        location: {
          exactLocation: "",
        },
        supportingEvidence: [],
      },
      evidenceLinks: "",
    };
  },
  computed: {
    isAdmin() {
      return this.appUser && this.appUser.usertype == 0;
    },
  },
  methods: {
    submitAIR() {
      if (confirm("Are you sure you want to submit this AIR for approval?")) {
        // Map evidence links to supportingEvidence array
        if (this.evidenceLinks) {
          this.airData.supportingEvidence = this.evidenceLinks.split(",").map(link => ({
            fileType: "image", // default
            url: link.trim(),
            description: "AIR Supporting Evidence"
          })).filter(item => item.url);
        }

        axios({
          method: "POST",
          url: "/crimereport/air/submit",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            id: this.crimeData._id,
            airData: this.airData,
          },
        })
          .then((response) => {
            if (response.data.status) {
              this.$dlg.alert(response.data.msg, () => {
                this.$emit("refresh");
              }, { messageType: "success" });
            } else {
              this.$dlg.alert(response.data.msg, () => {}, { messageType: "error" });
            }
          })
          .catch((err) => {
            console.error(err);
            this.$dlg.alert("Failed to submit AIR", () => {}, { messageType: "error" });
          });
      }
    },
    approveAIR() {
      if (confirm("Are you sure you want to approve this AIR?")) {
        axios({
          method: "POST",
          url: "/crimereport/air/approve",
          headers: {
            token: localStorage.getItem("token"),
          },
          data: {
            id: this.crimeData._id,
          },
        })
          .then((response) => {
            if (response.data.status) {
              this.$dlg.alert(response.data.msg, () => {
                this.$emit("refresh");
              }, { messageType: "success" });
            } else {
              this.$dlg.alert(response.data.msg, () => {}, { messageType: "error" });
            }
          })
          .catch((err) => {
            console.error(err);
            this.$dlg.alert("Failed to approve AIR", () => {}, { messageType: "error" });
          });
      }
    },
    downloadPDF() {
      axios({
        method: "POST",
        url: "/crimereport/air/export",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          id: this.crimeData._id,
        },
        responseType: "blob",
      })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = fileURL;
          link.download = `AIR_${this.crimeData.crimeReportID || 'Report'}.pdf`;
          link.click();
        })
        .catch((err) => {
          console.error(err);
          this.$dlg.alert("Failed to download PDF", () => {}, { messageType: "error" });
        });
    },
  },
};
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600 !important;
}
</style>
